/**
 * Shared renderer for every /chains/<slug> page.
 *
 * The route files under `src/routes/$locale/chains/` are one-liners that pass a
 * slug; all presentation and SEO live here. Live network facts are pulled from
 * chainData.json at render time, so a chain page cannot claim a chain id, RPC
 * or explorer that differs from what the app connects to.
 *
 * @module components/ChainPage
 * @category Components
 */

import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import Breadcrumb from '@/components/Breadcrumb'
import { handelTitle } from '@/utils'
import { setPageMetadata } from '@/utils/page-metadata'
import { createChainReferenceSchema } from '@/utils/metadata'
import {
  CHAIN_ARTICLES,
  chainDataFromId,
  explorerOf,
  caipOf,
  chainPageTitle,
  type ChainArticle,
} from '@/data/chains'

/** A contract address row rendered in the "Marketplace contracts" table. */
interface ContractRow {
  label: string
  value: string
  href?: string
}

const ZERO = '0x0000000000000000000000000000000000000000'

/** Contract addresses defined in chainData.json, for this chain. */
function contractsOf(article: ChainArticle): ContractRow[] {
  const row = chainDataFromId(article.chainId)
  if (!row) return []
  const candidates: ContractRow[] = [
    { label: 'AuctionManager (Diamond)', value: row.aManager || '' },
    { label: 'NFT Deployer', value: row.nftDeployer || '' },
    { label: 'Block Gas Token Issuer', value: row.bgti || '' },
    { label: 'MultiCall', value: row.MultiCall || '' },
    { label: 'Price Converter', value: row.PriceConverter || '' },
    { label: 'Wrapped Native Token', value: row.WNat || '' },
    { label: 'ERC-20 Factory', value: row.erc20Factory || '' },
  ]
  return candidates
    .filter((c) => c.value && c.value !== ZERO)
    .map((c) => ({
      ...c,
      href: explorerOf(row)
        ? `${explorerOf(row).replace(/\/$/, '')}/address/${c.value}`
        : undefined,
    }))
}

interface ChainPageProps {
  /** Slug of the article to render; must exist in CHAIN_ARTICLES. */
  slug: string
}

/**
 * Render a single chain's reference page.
 *
 * @param {Object} props
 * @param {string} props.slug - Slug matching an entry in CHAIN_ARTICLES
 * @returns {React.ReactElement}
 */
export default function ChainPage({ slug }: ChainPageProps) {
  const article = CHAIN_ARTICLES.find((a) => a.slug === slug)

  const title = article ? chainPageTitle(article) : 'Chain not found | VIRI'

  useEffect(() => {
    handelTitle(title)
  }, [title])

  if (!article) {
    return (
      <>
        <Breadcrumb text1="Home" text2="Chains" />
        <main className="main-content" style={{ paddingTop: '8px' }}>
          <div className="container-fluid py-4">
            <div className="pixel-card">
              <h1 className="h4 text-white">Chain not found</h1>
              <p className="text-secondary small">
                No chain page exists for “{slug}”.{' '}
                <Link to={'/about' as any} className="text-decoration-none">
                  Learn about the marketplace
                </Link>
                .
              </p>
            </div>
          </div>
        </main>
      </>
    )
  }

  const row = chainDataFromId(article.chainId)
  const explorer = explorerOf(row)
  const contracts = contractsOf(article)

  // Live values that chainData.json owns. Anything absent is simply not shown.
  const facts = [
    { label: 'Chain ID', value: row?.chainId ?? String(article.chainId) },
    { label: 'Namespace', value: row?.chainId ? caipOf(row) : `eip155:${article.chainId}` },
    { label: 'Native currency', value: row?.tokName ?? '—' },
    { label: 'Network family', value: row?.network ?? '—' },
  ]

  // Per-chain SEO description: the tagline alone is short, so pair it with the
  // chain's identity so the snippet reads usefully in search results.
  const metaDescription = [
    article.tagline,
    `${article.name} is chain id ${row?.chainId ?? article.chainId}${row?.tokName ? `, settled in ${row.tokName}` : ''}, supported on the VIRI NFT marketplace.`,
  ].join(' ')

  setPageMetadata({
    title,
    description: metaDescription,
    ogImage: '',
    ogType: 'article',
    twitterCard: 'summary_large_image',
    jsonLd: createChainReferenceSchema({
      name: article.name,
      headline: title.replace(/\s*\|\s*VIRI$/, ''),
      url: `https://stealthisnft.xyz/chains/${article.slug}`,
      description: article.tagline,
      chainId: row?.chainId ? caipOf(row) : `eip155:${article.chainId}`,
      nativeCurrency: row?.tokName,
      explorerUrl: explorer || undefined,
      consensus: article.specs.find((s) => s.label.toLowerCase() === 'consensus')?.value,
      testnet: article.testnet,
      breadcrumbs: [
        { name: 'Home', url: 'https://stealthisnft.xyz' },
        { name: 'Chains', url: 'https://stealthisnft.xyz/chains/flare' },
        { name: article.name, url: `https://stealthisnft.xyz/chains/${article.slug}` },
      ],
    }),
  })

  return (
    <>
      <Breadcrumb
        crumbs={[
          { name: 'Home', url: '/' },
          { name: 'Chains', url: '/chains/flare' },
          { name: article.name },
        ]}
      />

      <main className="main-content" style={{ paddingTop: '8px' }}>
        <div className="container-fluid py-4">
          <div className="row">
            <div className="col-12">

              {/* Header */}
              <div className="pixel-card mb-4">
                <div className="chain-page-head">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <h1 className="h3 mb-0 text-white">{article.name}</h1>
                      {article.testnet && (
                        <span className="badge bg-warning text-dark">Testnet</span>
                      )}
                    </div>
                    <p className="text-secondary mb-0">{article.tagline}</p>
                  </div>
                  {explorer && (
                    <a
                      className="btn btn-sm btn-outline-light"
                      href={explorer}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Explorer
                    </a>
                  )}
                </div>

                {article.testnet && (
                  <p className="chain-page-notice mt-3 mb-0">
                    <strong>Testnet.</strong> Tokens on this network have no real
                    value. Use it for development and integration testing, not for
                    real assets.
                  </p>
                )}
              </div>

              {/* Intro */}
              <div className="pixel-card mb-4">
                <p className="mb-0">{article.intro}</p>
              </div>

              {/* Live facts + specs */}
              <div className="row mb-4">
                <div className="col-lg-6 mb-3 mb-lg-0">
                  <div className="pixel-card h-100">
                    <h2 className="h5">Network</h2>
                    <dl className="chain-spec-list mb-0">
                      {facts.map((f) => (
                        <div key={f.label} className="chain-spec-row">
                          <dt>{f.label}</dt>
                          <dd>{f.value}</dd>
                        </div>
                      ))}
                      <div className="chain-spec-row">
                        <dt>RPC endpoint</dt>
                        <dd>
                          {row?.rpc ? (
                            <code className="text-break">{row.rpc}</code>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                      <div className="chain-spec-row">
                        <dt>Explorer</dt>
                        <dd>
                          {explorer ? (
                            <a href={explorer} target="_blank" rel="noopener noreferrer">
                              {row?.chainName || article.name}
                            </a>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="col-lg-6 mb-3 mb-lg-0">
                  <div className="pixel-card h-100">
                    <h2 className="h5">Technical</h2>
                    <dl className="chain-spec-list mb-0">
                      {article.specs.map((s) => (
                        <div key={s.label} className="chain-spec-row">
                          <dt>{s.label}</dt>
                          <dd>{s.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              </div>

              {/* Prose sections */}
              {article.sections.map((section) => (
                <div key={section.heading} className="pixel-card mb-4">
                  <h2 className="h5">{section.heading}</h2>
                  {section.body.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                  {section.links && section.links.length > 0 && (
                    <ul className="chain-link-list">
                      {section.links.map((l) => (
                        <li key={l.href}>
                          <a href={l.href} target="_blank" rel="noopener noreferrer">
                            {l.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* Marketplace contracts */}
              {contracts.length > 0 && (
                <div className="pixel-card mb-4">
                  <h2 className="h5">Marketplace contracts on {article.name}</h2>
                  <p className="text-secondary small">
                    Verified addresses from this marketplace&apos;s chain
                    configuration. Follow a link to inspect the contract on the
                    block explorer.
                  </p>
                  <dl className="chain-spec-list mb-0">
                    {contracts.map((c) => (
                      <div key={c.label} className="chain-spec-row">
                        <dt>{c.label}</dt>
                        <dd>
                          {c.href ? (
                            <a href={c.href} target="_blank" rel="noopener noreferrer">
                              <code className="text-break">{c.value}</code>
                            </a>
                          ) : (
                            <code className="text-break">{c.value}</code>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Marketplace note */}
              {article.marketplaceNote && (
                <div className="pixel-card mb-4">
                  <h2 className="h5">On this marketplace</h2>
                  <p className="mb-0">{article.marketplaceNote}</p>
                </div>
              )}

              {/* Links */}
              <div className="pixel-card mb-4">
                <h2 className="h5">Links</h2>
                <ul className="chain-link-list mb-0">
                  {article.links.map((l) => (
                    <li key={l.href}>
                      <a href={l.href} target="_blank" rel="noopener noreferrer">
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Other chains */}
              <div className="pixel-card mb-4">
                <h2 className="h5">Other chains</h2>
                <ul className="chain-link-list mb-0">
                  {CHAIN_ARTICLES.filter((a) => a.slug !== article.slug).map((a) => (
                    <li key={a.slug}>
                      <Link to={`/chains/${a.slug}` as any} className="text-decoration-none">
                        {a.name}
                        {a.testnet ? ' (testnet)' : ''}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>
        </div>
      </main>
    </>
  )
}